import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { httpClient, toApiError, type ApiError } from '@/api/httpClient'

/** Single-user session state, backed by an httpOnly auth cookie on the server. */
export const useAuthStore = defineStore('auth', () => {
  const username = ref<string | null>(null)
  const checked = ref(false)
  const error = ref<ApiError | null>(null)

  const isAuthenticated = computed(() => username.value !== null)

  /** Probes the server session once; safe to call repeatedly. */
  async function fetchSession(): Promise<boolean> {
    try {
      const { data } = await httpClient.get<{ username: string }>('/api/auth/me')
      username.value = data.username
    } catch {
      username.value = null
    } finally {
      checked.value = true
    }
    return isAuthenticated.value
  }

  async function login(name: string, password: string): Promise<boolean> {
    error.value = null
    try {
      const { data } = await httpClient.post<{ username: string }>('/api/auth/login', {
        username: name,
        password
      })
      username.value = data.username
      checked.value = true
      return true
    } catch (err) {
      error.value = toApiError(err)
      return false
    }
  }

  async function logout(): Promise<void> {
    try {
      await httpClient.post('/api/auth/logout')
    } catch {
      /* best effort — clear locally regardless */
    }
    username.value = null
  }

  /** Clears local auth state (e.g. after a 401 from an expired session). */
  function clear(): void {
    username.value = null
    checked.value = true
  }

  return { username, checked, error, isAuthenticated, fetchSession, login, logout, clear }
})
